import { format, money as makeMoney } from '@/lib/money/money'
import type { Source, Tone } from './spec/types'

/**
 * The non-visual half of the shared primitives.
 *
 * Split from `primitives.tsx` because a module that exports both components
 * and plain functions breaks fast refresh, and these three are imported by
 * nearly every block.
 */

/**
 * An amount's digits, with no currency merged in.
 *
 * Every caller renders the ISO code in its own adjacent span, because the
 * pairing has to survive a copy and a glance (invariant 4). Minor units in,
 * exponent from the ISO-4217 table — never assume two decimal places, and
 * never a float on the way through (invariant 3).
 *
 * Accepts a string so a caller holding minor units as text — the composer's
 * amount field, a JSON payload — can pass them straight through. Routing them
 * via `Number()` first would work today and silently lose precision the day an
 * amount passes 2^53; there is no reason to introduce a double on the path
 * when `money()` converts to `bigint` anyway.
 *
 * This is the ONLY formatter. A component reaching for `toFixed` is a
 * review-blocking error.
 */
export function formatMinor(minor: bigint | number | string, currency: string): string {
  return format(makeMoney(minor, currency), { withCurrency: false })
}

/**
 * Which store answered, and whether it may be trusted for money.
 *
 * Law 12 / CLAUDE.md §2 — analytics is badged with its freshness, money paths
 * are ledger-only. The badge exists so an analytics figure can never be
 * mistaken for an authoritative one. It is not decoration and must not be
 * dropped to tidy a layout.
 */
export function sourceBadge(src: Source | undefined): { label: string; tone: Tone } {
  switch (src) {
    case 'analytics':
      return { label: 'Analytics · ClickHouse', tone: 'v' }
    case 'provider':
      return { label: 'Provider record', tone: 'n' }
    case 'ledger':
      return { label: 'Ledger · authoritative', tone: 'g' }
    default:
      return { label: '', tone: 'n' }
  }
}

/** `m` selects the mono face; everything else inherits. Prototype convention. */
export function fontFor(flag: string | undefined): string {
  return flag === 'm' ? 'var(--k-mono)' : 'inherit'
}
